import { useTranslation } from 'react-i18next'

interface LoadingOverlayProps {
  message?: string
  size?: 'small' | 'medium' | 'large'
  overlay?: boolean
  fullScreen?: boolean
}

export default function LoadingOverlay({
  message,
  size = 'medium',
  overlay = true,
  fullScreen = false,
}: LoadingOverlayProps) {
  const { t } = useTranslation('common')

  const sizeClasses = {
    small: 'scale-75',
    medium: 'scale-100',
    large: 'scale-125',
  }

  const textSizeClasses = {
    small: 'text-sm',
    medium: 'text-base',
    large: 'text-lg',
  }

  const spinnerElement = (
    <div className={`flex flex-col items-center justify-center gap-12 ${sizeClasses[size]}`}>
      {/* Loading message - Main focus at top */}
      {message && (
        <div className="text-center z-10">
          <div className={`${size === 'small' ? 'text-2xl' : size === 'large' ? 'text-5xl' : 'text-4xl'} font-black text-transparent bg-clip-text bg-linear-to-r from-primary-500 via-secondary-500 to-primary-500 bg-size-200 animate-gradient tracking-tight`}>
            {message}
          </div>
        </div>
      )}

      {/* 3D Rotating Cube - Smaller and positioned as accent */}
      <div className="relative w-32 h-32 perspective-1000 z-0">
        <div
          className="w-full h-full relative preserve-3d"
          style={{
            animation: 'rotate-cube 3s infinite ease-in-out',
            transformStyle: 'preserve-3d',
          }}
        >
          {/* Cube faces */}
          {[
            { name: 'front', transform: 'rotateY(0deg) translateZ(64px)', bg: 'bg-primary-400' },
            { name: 'back', transform: 'rotateY(180deg) translateZ(64px)', bg: 'bg-secondary-400' },
            { name: 'right', transform: 'rotateY(90deg) translateZ(64px)', bg: 'bg-primary-500' },
            { name: 'left', transform: 'rotateY(-90deg) translateZ(64px)', bg: 'bg-secondary-500' },
            { name: 'top', transform: 'rotateX(90deg) translateZ(64px)', bg: 'bg-primary-600' },
            { name: 'bottom', transform: 'rotateX(-90deg) translateZ(64px)', bg: 'bg-secondary-600' },
          ].map((face) => (
            <div
              key={face.name}
              className={`absolute w-full h-full ${face.bg} opacity-70 border-2 border-white/30`}
              style={{
                transform: face.transform,
                backfaceVisibility: 'hidden',
              }}
            >
              <div className="w-full h-full flex items-center justify-center">
                <div className="w-12 h-12 border-2 border-white/40 animate-pulse"></div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Wave blocks animation */}
      <div className="flex gap-3 z-10">
        {[0, 1, 2, 3, 4, 5, 6].map((i) => (
          <div
            key={`block-${i}`}
            className={`w-3 h-12 rounded-lg ${
              i % 2 === 0 ? 'bg-primary-500' : 'bg-secondary-500'
            } shadow-lg`}
            style={{
              animation: 'wave 1.5s ease-in-out infinite',
              animationDelay: `${i * 0.1}s`,
            }}
          ></div>
        ))}
      </div>

      {/* Progress bar */}
      <div className="w-80 h-2 bg-gray-200 rounded-full overflow-hidden shadow-inner z-10">
        <div
          className="h-full bg-linear-to-r from-primary-500 via-secondary-500 to-primary-500 bg-size-200 shadow-lg"
          style={{
            animation: 'progress 2s ease-in-out infinite',
          }}
        ></div>
      </div>

      <style>{`
        .perspective-1000 {
          perspective: 1000px;
        }
        .preserve-3d {
          transform-style: preserve-3d;
        }
        .bg-size-200 {
          background-size: 200% 100%;
        }

        @keyframes rotate-cube {
          0% { transform: rotateX(0deg) rotateY(0deg); }
          25% { transform: rotateX(90deg) rotateY(90deg); }
          50% { transform: rotateX(180deg) rotateY(180deg); }
          75% { transform: rotateX(270deg) rotateY(270deg); }
          100% { transform: rotateX(360deg) rotateY(360deg); }
        }

        @keyframes wave {
          0%, 100% { transform: scaleY(0.3); opacity: 0.6; }
          50% { transform: scaleY(1); opacity: 1; }
        }

        @keyframes progress {
          0% { transform: translateX(-100%); }
          50% { transform: translateX(0%); }
          100% { transform: translateX(100%); }
        }

        @keyframes gradient {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }

        .animate-gradient {
          animation: gradient 3s ease infinite;
        }
      `}</style>
    </div>
  )

  if (!overlay) {
    return spinnerElement
  }

  // Full screen overlay (covers main content area only, not header/sidebar)
  // Note: Requires page component root to not have `position: relative`
  if (fullScreen) {
    return (
      <div className="absolute inset-0 bg-linear-to-br from-blue-50/95 via-purple-50/95 to-pink-50/95 backdrop-blur-md flex items-center justify-center z-[9999]">
        {spinnerElement}
      </div>
    )
  }

  // Regular overlay (covers parent container)
  return (
    <div className="absolute inset-0 bg-linear-to-br from-blue-50/95 via-purple-50/95 to-pink-50/95 backdrop-blur-md flex items-center justify-center z-[9998]">
      {spinnerElement}
    </div>
  )
}
